import React, { useEffect, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import styles from './index.module.css';
import EditorToolbar, { getModules, formats } from './EditorToolbar';

const JournalEditor = ({
    initialData,
    holder = 'editorjs',
    onReadyApi = () => {},
    onChange = () => {},
    onBlur = () => {},
    isNewNote = false,
    handleEntryDeleteClick = () => {},
    handleRewriteWithAI = () => {},
    handleMoveEntry = () => {},
    sidebar = false,
}) => {
    const quillRef = useRef(null);
    const [editorReady, setEditorReady] = React.useState(false);
    const [editorContent, setEditorContent] = React.useState(initialData || '');
    const toolbarId = `${holder}-toolbar`;
    const modules = getModules(toolbarId);
    const hasFormattedFirstLineRef = useRef(false);

    // Expose API when editor is ready
    useEffect(() => {
        if (editorReady && quillRef.current) {
            const editor = quillRef.current.getEditor();
            if (editor && editor.root) {
                onReadyApi({
                    save: async () => {
                        if (quillRef.current) {
                            const ed = quillRef.current.getEditor();
                            if (ed && ed.root) {
                                return ed.root.innerHTML;
                            }
                        }
                        return '';
                    },
                    render: async data => {
                        if (quillRef.current && data) {
                            const ed = quillRef.current.getEditor();
                            if (ed && ed.root) {
                                // If data is HTML string, set it directly
                                if (typeof data === 'string') {
                                    ed.root.innerHTML = data;
                                    // Sync internal state
                                    setEditorContent(data);
                                    lastInitialDataRef.current = data;
                                }
                            }
                        }
                    },
                    clear: async () => {
                        if (quillRef.current) {
                            const ed = quillRef.current.getEditor();
                            if (ed) {
                                ed.setText('');
                                // Sync internal state
                                setEditorContent('');
                                lastInitialDataRef.current = null;
                                // Reset formatting flag when editor is cleared
                                hasFormattedFirstLineRef.current = false;
                            }
                        }
                    },
                });
            }
        }
    }, [editorReady, onReadyApi]);

    // Track the last initialData to prevent unnecessary resets
    const lastInitialDataRef = useRef(null);
    const isUserTypingRef = useRef(false);

    // Update editor content when initialData changes (but only if user isn't actively typing)
    useEffect(() => {
        if (initialData !== null && initialData !== undefined) {
            // Only update if initialData actually changed and user isn't typing
            if (lastInitialDataRef.current !== initialData && !isUserTypingRef.current) {
                setEditorContent(initialData);
                lastInitialDataRef.current = initialData;
            }
        } else {
            // Only clear if user isn't typing
            if (!isUserTypingRef.current) {
                setEditorContent('');
                lastInitialDataRef.current = null;
            }
        }
    }, [initialData]);

    const handleChange = html => {
        // ReactQuill's onChange receives the HTML content directly as a string
        if (html !== null && html !== undefined) {
            // Update internal state immediately so typing works
            setEditorContent(html);
            // Mark that user is typing
            isUserTypingRef.current = true;
            // Notify parent component
            onChange(html);
            // Reset typing flag after a short delay to allow initialData updates when needed
            setTimeout(() => {
                isUserTypingRef.current = false;
            }, 100);
        }
    };

    const handleBlur = () => {
        if (quillRef.current) {
            const editor = quillRef.current.getEditor();
            if (editor && editor.root) {
                onBlur(editor.root.innerHTML);
            }
        }
    };

    // Check if editor is ready after mount
    React.useEffect(() => {
        const checkReady = () => {
            if (quillRef.current) {
                const editor = quillRef.current.getEditor();
                if (editor && editor.root) {
                    setEditorReady(true);
                } else {
                    // Retry after a short delay
                    setTimeout(checkReady, 50);
                }
            }
        };
        checkReady();
    }, []);

    // Reset flag whenever isNewNote becomes true (when creating a new note)
    // Also reset when initialData is cleared (indicating a new note)
    useEffect(() => {
        if (isNewNote || initialData === null || initialData === '') {
            hasFormattedFirstLineRef.current = false;
        }
    }, [isNewNote, initialData]);

    // Set up keyboard handlers for new notes: first line h2 bold, subsequent lines regular
    useEffect(() => {
        if (!editorReady || !isNewNote || !quillRef.current) {
            return;
        }

        const editor = quillRef.current.getEditor();
        if (!editor) return;

        // Ensure flag is reset for new note
        hasFormattedFirstLineRef.current = false;

        // Format first line as h2 bold when user starts typing
        const handleTextChange = (delta, oldDelta, source) => {
            if (!isNewNote || source !== 'user') return;

            // Check if editor is empty - if so, reset the flag
            const text = editor.getText();
            if (text.trim().length === 0) {
                hasFormattedFirstLineRef.current = false;
                return;
            }

            // If already formatted, skip
            if (hasFormattedFirstLineRef.current) return;

            const selection = editor.getSelection();
            if (!selection) return;

            // Check if we're at the first line by checking if selection is at the start
            // or if the line starts at index 0
            const [line, offset] = editor.getLine(selection.index);
            if (!line) return;

            // Check if we're on the first line (line starts at index 0 or selection is at start)
            const lineStart = selection.index - offset;
            const isFirstLine = lineStart === 0;

            if (isFirstLine && !hasFormattedFirstLineRef.current) {
                // Get the length of the first line
                const firstLineLength = line.length();
                const firstLineText = editor.getText(0, firstLineLength);
                if (firstLineText.trim().length > 0) {
                    // Format first line as h2 and bold
                    editor.formatLine(0, firstLineLength, 'header', 2);
                    editor.formatText(0, firstLineLength, 'bold', true);
                    hasFormattedFirstLineRef.current = true;
                }
            }
        };

        // Handle Enter key to format next line as regular text
        const handleEnter = range => {
            if (!isNewNote) return true; // Allow default behavior for existing notes

            // Clear formatting before Enter so the new line doesn't inherit it
            editor.format('header', false);
            editor.format('bold', false);

            // After Enter is pressed, format the new line as regular text
            // Use multiple timeouts to catch the new line at different stages
            setTimeout(() => {
                const newSelection = editor.getSelection();
                if (newSelection) {
                    const newIndex = newSelection.index;

                    // Get the new line/block that was just created
                    const [newLine, offset] = editor.getLine(newIndex);
                    if (newLine) {
                        const lineStart = newIndex - offset;
                        const lineLength = Math.max(newLine.length(), 1);

                        // Remove all formatting from the new line
                        editor.formatLine(lineStart, lineLength, 'header', false);
                        editor.formatText(lineStart, lineLength, 'bold', false);

                        // Also clear format at cursor position
                        editor.format('header', false);
                        editor.format('bold', false);
                    }
                }
            }, 10);

            // Second pass to ensure formatting is removed
            setTimeout(() => {
                const newSelection = editor.getSelection();
                if (newSelection) {
                    const newIndex = newSelection.index;
                    const [newLine, offset] = editor.getLine(newIndex);
                    if (newLine) {
                        const lineStart = newIndex - offset;
                        const format = editor.getFormat(lineStart);
                        if (format.header || format.bold) {
                            editor.formatLine(lineStart, 1, 'header', false);
                            editor.formatText(lineStart, 1, 'bold', false);
                            editor.format('header', false);
                            editor.format('bold', false);
                        }
                    }
                }
            }, 50);

            return true; // Allow default Enter behavior
        };

        // Track when we're on a new line (not the first line) to ensure it's normal text
        const handleSelectionChange = () => {
            if (!isNewNote || !hasFormattedFirstLineRef.current) return;

            const selection = editor.getSelection();
            if (!selection) return;

            const [line, offset] = editor.getLine(selection.index);
            if (!line) return;

            const lineStart = selection.index - offset;

            // If we're not on the first line (lineStart > 0), ensure it's normal text
            if (lineStart > 0) {
                const format = editor.getFormat(selection.index);
                if (format.header || format.bold) {
                    const lineLength = Math.max(line.length(), 1);
                    editor.formatLine(lineStart, lineLength, 'header', false);
                    editor.formatText(lineStart, lineLength, 'bold', false);
                    editor.format('header', false);
                    editor.format('bold', false);
                }
            }
        };

        // Listen for text changes
        editor.on('text-change', handleTextChange);

        // Listen for selection changes to catch when user moves to a new line
        editor.on('selection-change', handleSelectionChange);

        // Add Enter key binding
        const keyboard = editor.getModule('keyboard');
        if (keyboard) {
            keyboard.addBinding({ key: 'Enter' }, handleEnter);
        }

        return () => {
            editor.off('text-change', handleTextChange);
            editor.off('selection-change', handleSelectionChange);
            // Reset flag on cleanup
            hasFormattedFirstLineRef.current = false;
        };
    }, [editorReady, isNewNote]);

    return (
        <div id={holder} className={styles.editorHolder}>
            <style>{`
                #${holder} .ql-container {
                    border-top: 1px solid var(--Neutral-Gray-6) !important;
                    border-radius: 0px !important;
                    margin-top: 11px;
                }
                #${holder} .ql-editor {
                    border: none !important;
                    padding-top:21px !important;
                }
                #${holder} .ql-formats {
                    margin-left: 0px !important;
                }
                #${holder} .ql-toolbar {
                    text-align: start !important;
                    border: none !important;
                }
                #${holder} .ql-snow .ql-toolbar {
                    border-bottom: 1px solid #f1f2f4 !important;
                }
            `}</style>
            <EditorToolbar
                sidebar={sidebar}
                toolbarId={toolbarId}
                handleEntryDeleteClick={handleEntryDeleteClick}
                handleRewriteWithAI={handleRewriteWithAI}
                handleMoveEntry={handleMoveEntry}
            />
            <ReactQuill
                ref={quillRef}
                theme="snow"
                modules={modules}
                formats={formats}
                placeholder="Start writing..."
                value={editorContent}
                onChange={handleChange}
                onBlur={handleBlur}
            />
        </div>
    );
};

export default JournalEditor;
